import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

import {
    ChevronDown,
    ChevronUp,
    Download,
    Ellipsis,
    EllipsisVertical,
    Pin,
    Share,
    SquarePlus,
    X,
} from "lucide-react";
import { useLocation } from "@remix-run/react";

import { cn } from "~/lib/utils";
import {
    isAndroidFirefox,
    isDesktopFirefox,
    isWindowsFirefox,
} from "~/lib/firefox-install";
import {
    isIOSChrome,
    isIOSDevice,
    isIOSSafari,
    isIOSSafari26OrHigher,
} from "~/lib/ios-install";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { useIsPractitioner } from "~/lib/user";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useUserAgent } from "~/context/UserAgentContext";

// AIDEV-NOTE: Fired by Chromium browsers (Android/desktop Chrome) so we can trigger
// the native install flow on demand. iOS Safari never fires this — there we open a
// version-specific instruction sheet (Share -> Add to Home Screen) instead.
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// AIDEV-NOTE: Which manual-install guide to show. Safari shows version-specific steps anchored
// to its bottom toolbar (share moved behind "•••" in iOS 26). iOS Chrome's share button is at
// the TOP, so its guide anchors to the top with an upward arrow. "other" = any other iOS browser
// (Edge/Firefox), generic bottom flow. "android-firefox" = Gecko on Android, which has no
// `beforeinstallprompt` but does expose a manual Install / Add to Home screen menu item.
type GuideState =
    | { browser: "safari"; is26: boolean }
    | { browser: "chrome" }
    | { browser: "other" }
    | { browser: "android-firefox" }
    | { browser: "windows-firefox" }
    | { browser: "desktop" };

// AIDEV-NOTE: Capture `beforeinstallprompt` at MODULE LOAD, not in a useEffect. Chrome
// fires it once, very early (often before React hydrates), so a component-mounted listener
// races and misses it — leaving the banner tap with nothing to fire. We stash the event the
// instant this module evaluates (root imports it early); `handleActivate` reads it at tap time.
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredInstallPrompt = e as BeforeInstallPromptEvent;
    });
    // Drop the stale captured prompt once the app is actually installed.
    window.addEventListener("appinstalled", () => {
        deferredInstallPrompt = null;
    });
}

// AIDEV-NOTE: Inline iOS control chip (mimics the toolbar/menu button referenced in a step).
function Chip({ children }: { children: ReactNode }) {
    return (
        <span className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-brand-forest-200 bg-brand-cream-50 px-1.5 py-0.5 align-middle text-xs font-medium text-brand-forest-700 [&_svg]:size-3.5">
            {children}
        </span>
    );
}

// AIDEV-NOTE: Build the ordered manual-install steps for the open guide. Each browser exposes
// "Add to Home Screen" differently — Safari 26 hides Share behind "•••", older Safari has Share
// in the bottom toolbar, and iOS Chrome's Share is at the top followed by "View More" -> scroll.
function buildSteps(guide: GuideState | null): ReactNode[] {
    const scrollHint = (
        <span className="mt-1 block text-brand-forest-400">
            (scroll down to find it)
        </span>
    );
    const addStep = (
        <>
            Tap{" "}
            <Chip>
                <SquarePlus /> Add to Home Screen
            </Chip>
            {scrollHint}
        </>
    );

    if (guide?.browser === "chrome") {
        return [
            <>
                Tap{" "}
                <Chip>
                    <Share />
                </Chip>{" "}
                at the top
            </>,
            <>
                Tap{" "}
                <Chip>
                    <ChevronDown /> View More
                </Chip>
            </>,
            addStep,
        ];
    }
    // AIDEV-NOTE: Android Firefox never fires `beforeinstallprompt`, so the only path is its
    // own menu: ⋮ -> "Install" (newer builds) or "Add to Home screen" (older). Desktop Firefox
    // has no equivalent — it's suppressed upstream in the `canInstall` gate.
    if (guide?.browser === "android-firefox") {
        return [
            <>
                Tap{" "}
                <Chip>
                    <EllipsisVertical />
                </Chip>{" "}
                in the toolbar
            </>,
            <>
                Choose{" "}
                <Chip>
                    <Download /> Install
                </Chip>{" "}
                or{" "}
                <Chip>
                    <SquarePlus /> Add to Home screen
                </Chip>
                {scrollHint}
            </>,
        ];
    }
    // AIDEV-NOTE: Windows Firefox "Taskbar Tabs" — the only desktop Firefox install path. The
    // entry point is a button in the address bar (page actions), not the ••• menu, and it opens
    // an app-style window that then offers to pin to the taskbar.
    if (guide?.browser === "windows-firefox") {
        return [
            <>
                Click{" "}
                <Chip>
                    <SquarePlus /> Add tab to taskbar
                </Chip>{" "}
                in the address bar
            </>,
            <>
                Confirm{" "}
                <Chip>
                    <Pin /> Pin to taskbar
                </Chip>
            </>,
            <>Open Parts Companion from your taskbar</>,
        ];
    }
    if (guide?.browser === "desktop") {
        return [
            <>
                Open your browser's{" "}
                <Chip>
                    <Ellipsis />
                </Chip>{" "}
                menu
            </>,
            <>
                Choose{" "}
                <Chip>
                    <Download /> Install app
                </Chip>{" "}
                or{" "}
                <Chip>
                    <SquarePlus /> Create shortcut
                </Chip>
            </>,
            <>Open Parts Companion from your apps or Dock</>,
        ];
    }
    if (guide?.browser === "safari" && guide.is26) {
        return [
            <>
                Tap{" "}
                <Chip>
                    <Ellipsis />
                </Chip>{" "}
                in the toolbar
            </>,
            <>
                Tap{" "}
                <Chip>
                    <Share /> Share
                </Chip>
            </>,
            addStep,
        ];
    }
    // Older Safari + generic "other" iOS browsers: Share lives in the bottom toolbar.
    return [
        <>
            Tap{" "}
            <Chip>
                <Share />
            </Chip>{" "}
            in the toolbar
        </>,
        addStep,
    ];
}

// AIDEV-NOTE: Prompt users to install the app. Two audiences: clients on mobile/tablet (Save to
// Home Screen) and practitioners on desktop (install the browser app/manual shortcut). Dismissed
// for 7 days via localStorage. Hidden when already running in standalone mode (i.e. installed).

const DISMISS_KEY = "install-prompt-dismissed-at";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isDismissed(): boolean {
    if (typeof window === "undefined") return true;
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        return Date.now() - Number(raw) < DISMISS_DURATION_MS;
    } catch {
        return false;
    }
}

function dismiss() {
    try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
        /* Safari private browsing — silently ignore */
    }
}

function isStandalone(): boolean {
    if (typeof window === "undefined") return true;
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        ("standalone" in window.navigator &&
            (window.navigator as Record<string, unknown>).standalone === true)
    );
}

export function InstallAppPrompt() {
    const location = useLocation();
    const { isPractitioner, isLoading: isPractitionerLoading } =
        useIsPractitioner();
    const { data: userDetails } = useUserDetails();
    const { userId } = useSupabaseSession();
    const { isMobile, isTablet, isDesktop } = useUserAgent();
    const [visible, setVisible] = useState(false);
    // AIDEV-NOTE: Guide state. null = closed. Safari gets version-specific steps (the share
    // button moved behind "•••" in iOS 26). Other iOS browsers (Chrome/Edge/Firefox — all
    // WebKit) can also Add to Home Screen since iOS 16.4, but their share button location
    // varies, so they get a generic flow.
    const [guide, setGuide] = useState<GuideState | null>(null);
    // AIDEV-NOTE: Gate the toast on a real install path existing — otherwise tapping it does
    // nothing. That path is either a captured `beforeinstallprompt` (Android/desktop Chromium)
    // or an iOS device (manual Share -> Add to Home Screen guide). Without this gate, browsers
    // that never fire the event and aren't iOS (or Chrome when it suppresses the prompt) would
    // show a dead toast. Android Firefox is explicitly allowed — it has a real manual path.
    const [canInstall, setCanInstall] = useState(false);
    const onboardingCompleted =
        userDetails?.profile?.onboarding_status === "completed";
    const isOnboardingRoute = location.pathname.startsWith("/onboarding");
    const suppressPrompt = !onboardingCompleted || isOnboardingRoute;

    useEffect(() => {
        // AIDEV-NOTE: Desktop Firefox has no `beforeinstallprompt` and no "Install app"/"Create
        // shortcut" menu item to point at. The exception is Windows, where "Taskbar Tabs" (Web
        // Apps) ships enabled by default — those users get a manual Taskbar Tabs guide. macOS has
        // nothing and Linux keeps it behind a pref, so suppress the affordance there entirely
        // rather than showing Chromium's steps, which don't exist in Firefox's menu.
        if (isDesktopFirefox() && !isWindowsFirefox()) return;
        if (
            deferredInstallPrompt !== null ||
            isIOSDevice() ||
            isAndroidFirefox() ||
            isDesktop
        ) {
            setCanInstall(true);
            return;
        }
        // `beforeinstallprompt` can arrive after mount — flip the gate when it does.
        const onPrompt = () => setCanInstall(true);
        window.addEventListener("beforeinstallprompt", onPrompt);
        return () =>
            window.removeEventListener("beforeinstallprompt", onPrompt);
    }, [isDesktop]);

    useEffect(() => {
        // AIDEV-NOTE: Never interrupt onboarding. Require the persisted completed status
        // and suppress all /onboarding routes, including while profile data is loading.
        if (suppressPrompt) {
            setVisible(false);
            setGuide(null);
            return;
        }

        // AIDEV-NOTE: Only prompt authenticated users — never pre-auth (landing/login).
        if (!userId) return;
        if (isPractitionerLoading) return;
        if (isStandalone()) return;
        if (isDismissed()) return;
        if (!canInstall) return;

        // AIDEV-NOTE: If the DB already records this surface as installed, don't prompt again.
        // pwaInstallSurface is "mobile" | "desktop" | "both" | null.
        const dbSurface = userDetails?.profile?.pwaInstallSurface;
        const isMobileSurface = isMobile || isTablet;
        const currentSurface = isMobileSurface ? "mobile" : "desktop";
        if (dbSurface === "both" || dbSurface === currentSurface) {
            return;
        }

        // AIDEV-NOTE: Audience — clients on mobile/tablet (Save to Home Screen), OR practitioners
        // on desktop. Desktop browsers that don't support the native prompt fall through to a
        // manual install/shortcut guide instead of showing a dead toast (notably Arc).
        const audienceEligible =
            (!isPractitioner && isMobileSurface) ||
            (isPractitioner && isDesktop);
        if (!audienceEligible) return;

        // Small delay so the page settles before the prompt slides in
        const timer = setTimeout(() => setVisible(true), 1500);
        return () => clearTimeout(timer);
    }, [
        userId,
        isPractitioner,
        isPractitionerLoading,
        isMobile,
        isTablet,
        isDesktop,
        canInstall,
        suppressPrompt,
        userDetails?.profile?.pwaInstallSurface,
    ]);

    const handleDismiss = useCallback(() => {
        setVisible(false);
        dismiss();
    }, []);

    const handleActivate = useCallback(async () => {
        const promptEvent = deferredInstallPrompt;
        // Android/desktop Chromium: trigger the native install prompt directly.
        if (promptEvent) {
            deferredInstallPrompt = null;
            try {
                const promptStartedAt = Date.now();
                await promptEvent.prompt();
                const choice = await promptEvent.userChoice;
                if (
                    choice.outcome === "dismissed" &&
                    Date.now() - promptStartedAt < 750
                ) {
                    setGuide({ browser: "desktop" });
                    return;
                }
                if (choice.outcome === "accepted") {
                    setVisible(false);
                } else {
                    setVisible(false);
                }
            } catch {
                setGuide({ browser: "desktop" });
            }
            return;
        }
        // iOS: no programmatic install for any browser (all WebKit). Show manual steps —
        // Safari gets version-specific bottom-toolbar steps; Chrome anchors to its top share
        // button; other iOS browsers get a generic flow.
        if (isIOSSafari()) {
            setGuide({ browser: "safari", is26: isIOSSafari26OrHigher() });
        } else if (isIOSChrome()) {
            setGuide({ browser: "chrome" });
        } else if (isIOSDevice()) {
            setGuide({ browser: "other" });
        } else if (isAndroidFirefox()) {
            setGuide({ browser: "android-firefox" });
        } else if (isWindowsFirefox()) {
            setGuide({ browser: "windows-firefox" });
        } else {
            setGuide({ browser: "desktop" });
        }
    }, []);

    if (suppressPrompt || !visible) return null;

    // AIDEV-NOTE: Per-browser manual-install steps. iOS 26 moved Share behind a "•••" (More)
    // toolbar button; iOS Chrome's Share is at the top, then "View More" -> scroll.
    const steps: ReactNode[] = buildSteps(guide);

    // AIDEV-NOTE: Chrome's share button is at the TOP, so anchor the guide to the top with an
    // upward arrow; Safari/other anchor to the bottom toolbar with a downward arrow.
    const anchorTop = guide?.browser === "chrome";

    return (
        <>
            {/* AIDEV-NOTE: Wrapper spans the full viewport width (inset-x-0 z-50) but the card
                is only max-w-md — pointer-events-none keeps the transparent strips either side
                from invisibly swallowing clicks; the card re-enables its own events. */}
            <div
                className={cn(
                    "pointer-events-none fixed inset-x-0 bottom-0 z-50 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]",
                    "animate-in slide-in-from-bottom-4 fade-in duration-300",
                )}
            >
                <div
                    role="button"
                    tabIndex={0}
                    onClick={handleActivate}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleActivate();
                        }
                    }}
                    className="pointer-events-auto mx-auto flex max-w-md cursor-pointer items-center gap-3 rounded-xl border border-brand-forest-200 bg-white px-3 py-3 shadow-lg transition-colors hover:bg-brand-cream-50"
                >
                    <div className="flex flex-1 items-center justify-start gap-3">
                        <div className="relative shrink-0">
                            <img
                                src="/apple-touch-icon.png"
                                alt=""
                                className="size-10 rounded-lg"
                            />
                            <span className="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full border-2 border-white bg-brand-forest-700">
                                <Download className="size-2 translate-x-[0.1px] text-white" />
                            </span>
                        </div>
                        <p className="text-sm font-medium text-brand-forest-800">
                            {isDesktop
                                ? "Install the Parts Companion app"
                                : "Save App to Homescreen"}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDismiss();
                        }}
                        className="shrink-0 rounded-md p-1 text-brand-forest-400 transition-colors hover:bg-brand-cream-100 hover:text-brand-forest-600"
                        aria-label="Dismiss"
                    >
                        <X className="size-4" />
                    </button>
                </div>
            </div>

            {guide && (
                <div
                    className={cn(
                        "fixed inset-0 z-[60] flex flex-col bg-black/40 px-4 animate-in fade-in",
                        anchorTop
                            ? "justify-start pt-[max(1rem,env(safe-area-inset-top))]"
                            : "justify-end pb-[max(1rem,env(safe-area-inset-bottom))]",
                    )}
                    onClick={() => setGuide(null)}
                >
                    {/* AIDEV-NOTE: Chrome's Share is at the TOP — point the bouncing arrow up,
                        above the card, at the same x-position as the bottom-anchored one. */}
                    {anchorTop && (
                        <div className="pointer-events-none relative mb-3 h-8 text-white">
                            <ChevronUp className="absolute bottom-0 left-[91.5%] size-8 -translate-x-1/2 animate-bounce" />
                        </div>
                    )}
                    <div
                        className={cn(
                            "relative mx-auto w-full max-w-sm rounded-2xl border border-brand-forest-200 bg-white p-5 shadow-xl animate-in",
                            anchorTop
                                ? "slide-in-from-top-4"
                                : "slide-in-from-bottom-4",
                        )}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setGuide(null)}
                            className="absolute right-3 top-3 rounded-md p-1 text-brand-forest-400 transition-colors hover:bg-brand-cream-100 hover:text-brand-forest-600"
                            aria-label="Close"
                        >
                            <X className="size-4" />
                        </button>
                        <div className="mb-4 flex items-center gap-3">
                            <img
                                src="/apple-touch-icon.png"
                                alt=""
                                className="size-10 shrink-0 rounded-lg"
                            />
                            <div>
                                <p className="text-sm font-semibold text-brand-forest-800">
                                    Add to your Home Screen
                                </p>
                                <p className="text-xs text-brand-forest-500">
                                    Install Parts Companion as an app
                                </p>
                            </div>
                        </div>
                        <ol className="space-y-3">
                            {steps.map((step, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 text-sm text-brand-forest-700"
                                >
                                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand-terra-100 text-xs font-semibold text-brand-terra-700">
                                        {i + 1}
                                    </span>
                                    <span className="leading-6">{step}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                    {/* AIDEV-NOTE: Bounces toward Safari's bottom toolbar. iOS 26 hides Share/Add
                        behind the "•••" button at the toolbar's right edge, so aim the arrow there
                        (~85%); earlier Safari / other browsers have Share centered. */}
                    {!anchorTop && (
                        <div className="pointer-events-none relative mt-3 h-8 text-white">
                            <ChevronDown
                                className={cn(
                                    "absolute top-0 size-8 -translate-x-1/2 animate-bounce",
                                    guide.browser === "safari" && guide.is26
                                        ? "left-[85%]"
                                        : "left-1/2",
                                )}
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
